import * as React from "react";
import { connect } from "react-redux";
import * as _ from "underscore";
import { Button, Select, SelectItem, SelectItemGroup } from "carbon-components-react";
import { Save16 } from "@carbon/icons-react";

import {
  DocTypes,
  UpdatePropertyTypeRequest,
  PropertyTypesLocatedInAProject,
} from "@eave/shared/types/api";
import APIClient from "@eave/borrower/web_modules/APIClient";
import {
  ProjectClassificationsFannieMaeDisplayValues,
  ProjectClassificationsFreedieMacDisplayValues,
  MortgageCompanies,
} from "@eave/shared/types/api/project-classifications";

import { loadApp } from "underwriter_dashboard/actions";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import AppraisalFields from "underwriter_dashboard/hurdle_editors/PropertyType/AppraisalFields";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");

require(`../../../../assets/stylesheets/components/underwriter_dashboard/hurdle_editors/\
property_type.scss`);

interface PropertyTypeProps extends HurdleProps {
  dispatch: Function;
}

class PropertyTypeEditor extends React.Component<PropertyTypeProps, any> {
  constructor(props) {
    super(props);
    this.state = this.initState(props);
  }

  initState(props) {
    const { app } = props;
    const {
      appraisal,
      level_of_property_review,
      appraisal_report_form_number,
      operator_subject_property_type: subjectPropertyType,
      property_is_located_within_a_project: isLocatedWithinAProject,
      project_classification: projectClassification,
    } = app;
    const appraisalPropertyType = appraisal && appraisal.property_type_computed;
    const titleCommitment = this.docFromProps(props);
    return {
      subject_property_type: subjectPropertyType,
      is_located_within_a_project: isLocatedWithinAProject,
      project_classification: projectClassification,
      appraisal_property_type: appraisalPropertyType,
      level_of_property_review,
      appraisal_report_form_number,
      title_commitment_property_type: this.getTitleCommitmentPropertyType(titleCommitment),
    };
  }

  componentDidMount() {
    this.getPropertyTypeOptions();
  }

  getTitleCommitmentPropertyType(titleCommitment) {
    let propertyType = "";
    if (titleCommitment) {
      const section = _.find(
        titleCommitment.sections,
        (section: any) => section.section_key === "property",
      );
      if (section) {
        const field = _.find(section.fields, (field: any) => field.field_key === "property_type");
        if (field) {
          propertyType = field.field_value;
        }
      }
    }
    return propertyType;
  }

  // TODO (mike) copied from Title Commitment Hurdle, but perhaps this should
  // sort by updated_at instead? Shouldn't make a difference, since the one displayed
  // on Title Commitment Hurdle will be the one the operator has the opportunity to edit.
  // Many hurdle editors on the frontend resolve the "right" doc of type by id, so leaving for now.
  docFromProps(props) {
    return (
      _.chain(props.app.documents)
        .filter((d: any) => d.type === DocTypes.TitleCommitment)
        // most recently uploaded title commitment is displayed.
        .sortBy((d: any) => -1 * parseInt(d.id, 10))
        .first()
        .value()
    );
  }

  setPropertyType = e => {
    if (!e || !e.target.value) {
      this.setState({ subject_property_type: "" });
      return;
    }

    this.setState({ subject_property_type: e.target.value });
    // Default is_located_within_a_project based on the property type
    if (PropertyTypesLocatedInAProject.includes(e.target.value)) {
      this.setState({ is_located_within_a_project: true });
    } else {
      this.setState({ is_located_within_a_project: false, project_classification: "" });
    }
  };

  setIsLocatedWithinAProject = e => {
    const inputValue = JSON.parse(e.target.value) as boolean; // cool hack!

    this.setState(prevState => ({
      ...prevState,
      project_classification: inputValue ? prevState.project_classification : "",
      is_located_within_a_project: inputValue,
    }));
  };

  setProjectClassification = e => {
    this.setState({ project_classification: e.target.value });
  };

  setLevelOfPropertyReview = e => {
    this.setState({ level_of_property_review: e.target.value });
  };

  setAppraisalReportFormNumber = e => {
    this.setState({ appraisal_report_form_number: e.target.value });
  };

  onSave() {
    const { app, dispatch } = this.props;
    const {
      subject_property_type,
      is_located_within_a_project,
      project_classification,
      level_of_property_review,
      appraisal_report_form_number,
    } = this.state;

    if (is_located_within_a_project && project_classification === "") {
      return;
    }

    const data: UpdatePropertyTypeRequest = {
      subject_property_type,
      is_located_within_a_project,
      project_classification,
      level_of_property_review,
      appraisal_report_form_number,
    };

    const url = `/loan_applications/${app.id}/hurdles/operator_subject_property_type`;
    APIClient.post(url, data)
      .then(() => loadApp(dispatch, app.id))
      .catch(err => alert(err.meta.error_detail));
  }

  getPropertyTypeOptions() {
    const { app } = this.props;
    APIClient.get(`/underwriting_subject_property_type_options`, response => {
      this.setState(response);
    });
  }

  renderPropertyTypes() {
    const {
      subject_property_type,
      is_located_within_a_project,
      project_classification,
      appraisal_property_type,
      title_commitment_property_type,
      underwriting_subject_property_type_options,
    } = this.state;
    return (
      <table className="table">
        <thead>
          <tr>
            <th className="col-lg-2">Source</th>
            <th className="col-lg-2">PropertyType</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Operator-specified</td>
            <td>
              <Select
                id="subject_property_type"
                name="subject_property_type"
                value={subject_property_type ?? ""}
                onChange={this.setPropertyType}
              >
                <SelectItem text="Select..." value="" />
                {underwriting_subject_property_type_options?.map(v => (
                  <SelectItem key={v.value} value={v.value} text={v.label} />
                ))}
              </Select>
            </td>
          </tr>
          <tr>
            <td>Is this property located in a project?</td>
            <td>
              <Select
                id="is_located_within_a_project"
                value={is_located_within_a_project}
                onChange={this.setIsLocatedWithinAProject}
              >
                <SelectItem value={false} text="No" />
                <SelectItem value={true} text="Yes" />
              </Select>
            </td>
          </tr>
          {is_located_within_a_project && (
            <tr>
              <td>Select the project classification</td>
              <td>
                <Select
                  id="project-classification"
                  name="project-classification"
                  onChange={this.setProjectClassification}
                  invalidText="Please choose a category"
                  invalid={is_located_within_a_project && project_classification === ""}
                  defaultValue={project_classification}
                >
                  <SelectItem text="Select a classification" value="" />
                  <SelectItemGroup label={MortgageCompanies.FannieMae}>
                    {Object.keys(ProjectClassificationsFannieMaeDisplayValues).map(v => (
                      <SelectItem
                        key={v}
                        text={ProjectClassificationsFannieMaeDisplayValues[v] || v}
                        value={v}
                      />
                    ))}
                  </SelectItemGroup>
                  <SelectItemGroup label={MortgageCompanies.FreddieMac}>
                    {Object.keys(ProjectClassificationsFreedieMacDisplayValues).map(v => (
                      <SelectItem
                        key={v}
                        text={ProjectClassificationsFreedieMacDisplayValues[v] || v}
                        value={v}
                      />
                    ))}
                  </SelectItemGroup>
                </Select>
              </td>
            </tr>
          )}
          <tr>
            <td>Appraisal</td>
            <td>{appraisal_property_type}</td>
          </tr>
          <tr>
            <td>Title Commitment</td>
            <td>{title_commitment_property_type}</td>
          </tr>
        </tbody>
      </table>
    );
  }

  render() {
    const { app, hurdle, onHide } = this.props;
    const {
      is_located_within_a_project,
      project_classification,
      level_of_property_review,
      appraisal_report_form_number,
    } = this.state;
    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        <p>
          This hurdle allows the operator to override the subject property type as ingested from the
          title commitment &amp; appraisal.
        </p>
        <p>
          In terms of what will render on disclosures, the sources of property type have the
          following priority (highest to lowest): operator-specified, appraisal, title commitment.
        </p>
        <p>
          For example, if there is no operator-specified property type value, but we have a property
          type value from the appraisal, the value from the appraisal will render on disclosures.
        </p>

        {this.renderPropertyTypes()}

        <AppraisalFields
          setAppraisalReportFormNumber={this.setAppraisalReportFormNumber}
          setLevelOfPropertyReview={this.setLevelOfPropertyReview}
          levelOfPropertyReview={level_of_property_review}
          appraisalReportFormNumber={appraisal_report_form_number}
        />
        <footer className={styles.footer}>
          <Button kind="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button
            disabled={is_located_within_a_project && project_classification === ""}
            kind="primary"
            onClick={this.onSave.bind(this)}
            renderIcon={Save16}
          >
            Save changes
          </Button>
        </footer>
      </HurdleView>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  app: ownProps.app,
  hurdle: ownProps.hurdle,
  onHide: ownProps.onHide,
});

export default connect(mapStateToProps)(PropertyTypeEditor);
